import React, {useEffect, useState} from 'react';
import styled from "styled-components";
import {get, isEqual} from "lodash";
import {ErrorMessage} from "@hookform/error-message";
import {Eye, EyeOff} from "react-feather";
import Label from "../../../../components/ui/label";
import classNames from "classnames";
import {toUpperLatinAlphanumeric, toUpperLatinLetters} from "../../../../utils/formatUpperLatin";

const Styled = styled.div`
  .form-input {
    display: block;
    min-width:  ${({sm}) => sm ? 'unset': '275px'};
    width: 100%;
    padding:  ${({sm}) => sm ? '6px': '12px 18px'};
    color: #000;
    font-size: ${({sm}) => sm ? '14px': '16px'};
    border: 1px solid #BABABA;
    border-radius: 5px;
    outline: none;
    font-family: 'Gilroy-Regular', sans-serif;

    &.error {
      border-color: #ef466f;
    }

    &:focus {
      border-color: #13D6D1;
    }
  }

  .password-toggle-wrap {
    position: relative;

    .form-input {
      padding-right: 44px;
    }
  }

  .password-toggle-btn {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    color: #555;
    line-height: 0;

    &:hover {
      color: #000;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  }
`;
const Input = ({
                   register,
                   disabled = false,
                   name,
                   errors,
                   params,
                   property,
                   defaultValue,
                   getValues,
                   watch,
                   label,
                   setValue,
                   getValueFromField = () => {
                   },
                   sm = false,
                   ...rest
               }) => {
    const showPasswordToggle = isEqual(get(property, 'type'), 'password') && get(property, 'showPasswordToggle');
    const [passwordVisible, setPasswordVisible] = useState(false);
    const uppercaseLatin = get(property, 'uppercaseLatin');
    const formatUpperLatinValue = (value) => {
        if (!uppercaseLatin) {
            return value;
        }
        return isEqual(uppercaseLatin, 'alphanumeric')
            ? toUpperLatinAlphanumeric(value)
            : toUpperLatinLetters(value);
    };
    const {onChange: registerOnChange, onBlur, ref, name: fieldName} = register(name, params);
    const handleInputChange = (event) => {
        if (uppercaseLatin) {
            const formatted = formatUpperLatinValue(event.target.value);
            event.target.value = formatted;
            setValue(name, formatted, {shouldValidate: true, shouldDirty: true});
        }
        registerOnChange(event);
    };

    useEffect(() => {
        setValue(name, defaultValue)
    }, [defaultValue])

    useEffect(() => {
        getValueFromField(getValues(name), name);
    }, [watch(name)]);

    return (
        <Styled {...rest} sm={sm}>
            <div className="form-group">
                {!get(property, 'hideLabel', false) && <Label sm={sm}
                    className={classNames({required: get(property, 'hasRequiredLabel', get(params, 'required'))})}>{label ?? name}</Label>}
                {isEqual(get(property, 'type'), 'number') ? <input
                    className={classNames('form-input', {error: get(errors, `${name}`, false)})}
                    name={name}
                    {...register(name, params)}
                    placeholder={get(property, "placeholder")}
                    type={'number'}
                    disabled={get(property, "disabled")}
                    defaultValue={defaultValue}
                    min={get(property, 'min', 0)}
                    max={get(property, 'max', 100)}
                    step={get(property, 'step',0.01)}
                /> : showPasswordToggle ? (
                    <div className="password-toggle-wrap">
                        <input
                            className={classNames('form-input', {error: get(errors, `${name}`, false)})}
                            name={name}
                            {...register(name, params)}
                            placeholder={get(property, "placeholder")}
                            type={passwordVisible ? 'text' : 'password'}
                            disabled={get(property, "disabled")}
                            defaultValue={defaultValue}
                            autoComplete={get(property, 'autoComplete', 'current-password')}
                        />
                        <button
                            type="button"
                            className="password-toggle-btn"
                            tabIndex={-1}
                            disabled={get(property, "disabled")}
                            onClick={() => setPasswordVisible((v) => !v)}
                            aria-label={passwordVisible ? 'Hide password' : 'Show password'}
                        >
                            {passwordVisible ? <EyeOff size={20}/> : <Eye size={20}/>}
                        </button>
                    </div>
                ) : <input
                    ref={ref}
                    className={classNames('form-input', {error: get(errors, `${name}`, false), 'text-uppercase': !!uppercaseLatin})}
                    name={fieldName}
                    onBlur={onBlur}
                    onChange={uppercaseLatin ? handleInputChange : registerOnChange}
                    placeholder={get(property, "placeholder")}
                    type={get(property, "type", "text")}
                    disabled={get(property, "disabled")}
                    defaultValue={defaultValue}
                />}
                <ErrorMessage
                    errors={errors}
                    name={name}
                    render={({messages = 'Field is required'}) => {
                        if (get(get(errors, name), 'type') == 'required') {
                            messages = `${label ?? name} is required`;
                        }
                        if (get(get(errors, name), 'type') == "pattern") {
                            messages = `${label ?? name}  ${get(get(errors, name), 'message')}`;
                        }
                        if (get(get(errors, name), 'type') == 'manual') {
                            messages = `${label ?? name} ${errors[name].message}`;
                        }
                        return <small className="form-error-message"> {messages}</small>;
                    }}
                />
            </div>
        </Styled>
    );
};

export default Input;
